/**
 * Pirinola — public-survey adapter around the design-authored canvas
 * (`_vendor/PirinolaCanvas`, ported from the root `/games` library).
 *
 * Picks the winner once via the shared `pickPrize`, builds the six toton
 * faces from the live prize pool (winner included, padded to 6), resolves the
 * brand, and bridges `onRevealed` → `onWin`. Visuals live in the canvas.
 */
import { useMemo } from 'react';
import type { GamePlayProps } from './pickPrize';
import { pickPrize } from './pickPrize';
import { Pirinola as PirinolaCanvas, type PirinolaEdition } from './_vendor/PirinolaCanvas';
import { resolveBrand } from './_shared/resolveBrand';
import { gameLang, toSegment, buildPool } from './_shared/segments';
import { GameEmpty } from './_shared/GameEmpty';

export function Pirinola({ items, primary, accent, onWin, lang, logo, config }: GamePlayProps) {
  const valid = useMemo(() => items.filter((i) => i.available > 0), [items]);
  const winnerIdx = useMemo(() => pickPrize(valid), [valid]);

  if (valid.length === 0 || winnerIdx < 0) return <GameEmpty />;

  const winner = valid[winnerIdx];
  const brand = resolveBrand({ primary, accent, logo });
  // The toton always has 6 faces; winner included, pool cycled to fill.
  const faces = buildPool(valid, winner, 6);

  return (
    <div className="flex w-full justify-center">
      <PirinolaCanvas
        prize={toSegment(winner)}
        brand={brand}
        faces={faces}
        edition={(config?.edition as PirinolaEdition | undefined) ?? 'traditional'}
        wordmark={typeof config?.wordmark === 'string' ? config.wordmark : undefined}
        language={gameLang(lang)}
        onRevealed={() => onWin(winner)}
      />
    </div>
  );
}
